import { MAX_WAVES, TOPO_FRAGMENT_SHADER } from "./shaders/topo.frag";
import { TOPO_VERTEX_SHADER } from "./shaders/topo.vert";
import type { ContourOptions, WaveParams } from "./types";

/** Everything the renderer needs to draw one frame. */
export interface RenderState {
  waves: readonly WaveParams[];
  extent: number;
  timeSeconds: number;
  contour: ContourOptions | null;
  /** Background/clear color as RGBA in [0, 1]. Transparent by default. */
  background: [number, number, number, number];
  /** Crest sharpening (Stokes 2nd harmonic) in [0, 1]. */
  steepness: number;
}

function compileShader(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error("Sargasso: could not create shader");
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Sargasso: shader compile failed: ${log ?? "unknown error"}`);
  }
  return shader;
}

/**
 * Owns the WebGL2 context and draws the contour lines. If a WebGL2 context
 * cannot be acquired, {@link supported} is false and all draw calls are no-ops,
 * so the surrounding controller (and its CPU probe) still work.
 */
export class TopoRenderer {
  readonly supported: boolean;
  private readonly gl: WebGL2RenderingContext | null;
  private program: WebGLProgram | null = null;
  private readonly locations = new Map<string, WebGLUniformLocation | null>();

  constructor(private readonly canvas: HTMLCanvasElement) {
    const gl = canvas.getContext("webgl2", {
      antialias: true,
      alpha: true,
      premultipliedAlpha: false,
    });
    this.gl = gl;
    this.supported = gl !== null;
    if (gl) this.init(gl);
  }

  private init(gl: WebGL2RenderingContext): void {
    const vert = compileShader(gl, gl.VERTEX_SHADER, TOPO_VERTEX_SHADER);
    const frag = compileShader(gl, gl.FRAGMENT_SHADER, TOPO_FRAGMENT_SHADER);
    const program = gl.createProgram();
    if (!program) throw new Error("Sargasso: could not create program");
    gl.attachShader(program, vert);
    gl.attachShader(program, frag);
    gl.linkProgram(program);
    // Shaders can be detached/deleted once linked.
    gl.deleteShader(vert);
    gl.deleteShader(frag);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const log = gl.getProgramInfoLog(program);
      gl.deleteProgram(program);
      throw new Error(`Sargasso: program link failed: ${log ?? "unknown error"}`);
    }
    this.program = program;
    gl.useProgram(program);
  }

  private loc(name: string): WebGLUniformLocation | null {
    const cached = this.locations.get(name);
    if (cached !== undefined) return cached;
    const gl = this.gl;
    const location = gl && this.program ? gl.getUniformLocation(this.program, name) : null;
    this.locations.set(name, location);
    return location;
  }

  /** Resize the drawing buffer and viewport. */
  setSize(width: number, height: number): void {
    const w = Math.max(1, Math.floor(width));
    const h = Math.max(1, Math.floor(height));
    if (this.canvas.width !== w) this.canvas.width = w;
    if (this.canvas.height !== h) this.canvas.height = h;
    this.gl?.viewport(0, 0, w, h);
  }

  render(state: RenderState): void {
    const gl = this.gl;
    if (!gl || !this.program) return;

    gl.useProgram(this.program);
    gl.uniform2f(this.loc("u_resolution"), this.canvas.width, this.canvas.height);
    gl.uniform1f(this.loc("u_time"), state.timeSeconds);
    gl.uniform1f(this.loc("u_extent"), state.extent);
    gl.uniform1f(this.loc("u_steepness"), state.steepness);
    gl.uniform4fv(this.loc("u_background"), state.background);

    const count = Math.min(state.waves.length, MAX_WAVES);
    const dir = new Float32Array(count);
    const wavelength = new Float32Array(count);
    const amplitude = new Float32Array(count);
    const speed = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      const wave = state.waves[i];
      if (!wave) continue;
      dir[i] = wave.direction;
      wavelength[i] = wave.wavelength;
      amplitude[i] = wave.amplitude;
      speed[i] = wave.speed;
    }
    gl.uniform1i(this.loc("u_waveCount"), count);
    gl.uniform1fv(this.loc("u_dir[0]"), dir);
    gl.uniform1fv(this.loc("u_wavelength[0]"), wavelength);
    gl.uniform1fv(this.loc("u_amplitude[0]"), amplitude);
    gl.uniform1fv(this.loc("u_speed[0]"), speed);

    const contour = state.contour;
    gl.uniform1i(this.loc("u_contourEnabled"), contour ? 1 : 0);
    if (contour) {
      gl.uniform1f(this.loc("u_contourInterval"), contour.interval);
      gl.uniform1f(this.loc("u_contourWidth"), contour.width);
      gl.uniform4fv(this.loc("u_contourColor"), contour.color);
      gl.uniform1i(this.loc("u_emphasisEvery"), contour.emphasisEvery ?? 0);
      gl.uniform1f(this.loc("u_emphasisWidth"), contour.emphasisWidth ?? contour.width * 2);
      gl.uniform4fv(this.loc("u_emphasisColor"), contour.emphasisColor ?? contour.color);
    }

    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  destroy(): void {
    const gl = this.gl;
    if (gl && this.program) gl.deleteProgram(this.program);
    this.program = null;
    this.locations.clear();
    gl?.getExtension("WEBGL_lose_context")?.loseContext();
  }
}
